'use client';

import {
  AuthContext,
  LocaleContext,
  ThemeContext,
  useBlockResolver,
  useCurrentUser,
  useEnabledPlugins,
  useLocale,
  useThemeId,
} from './contexts';
import { Trans, useTranslation } from 'react-i18next';
import { BlockErrorBoundary } from './BlockErrorBoundary';
import { BlockIdentityContext, BlockRootStampedContext } from './blockIdentity';
import { isVisible } from './i18n';
import { localizeTree } from './localize';
import type { CurrentUser } from './auth-types';
import type { BlockInstance, BlockVisibilityProps, PageConfig, SlotSpec, SlotValue } from './types';

/**
 * Top-level renderer for a portal page. Walks the template's slots in
 * order, resolves each block instance against the active resolver +
 * theme, and renders inside a per-block error boundary.
 *
 * <p>Sets the four contexts ({@code Auth}, {@code Locale}, {@code Theme})
 * once at the top so nested blocks can read them without prop drilling.
 * Block components don't need to receive {@code themeId} / {@code locale}
 * / {@code currentUser} via props.
 *
 * <p>Each block INSTANCE can carry a {@code props._visibility} gate —
 * {@code requireAuth} / {@code requirePerms} / {@code requireDynamicGroupIds},
 * the same {@code Gated} axes shell menus already use — authored via
 * the admin page designer's "Visibility" panel, independent of the
 * block's own JSON Schema (see {@link BlockVisibilityProps}). A block
 * instance that fails the gate is skipped exactly like an empty slot;
 * {@code findFirstBlock} only considers blocks that pass, so the bleed
 * marker still lands on the first block an anonymous/ungated visitor
 * actually sees.
 *
 * <p>First-block bleed: the first block of the first non-empty slot
 * gets a {@code data-bleeds-under-header="true"} attribute when the
 * caller supplies {@code firstBlockBleeds}. The shell uses CSS
 * {@code :has()} to inspect this and decide whether to reserve top
 * padding under a floating overlay header. Direct port of the bug-fix
 * documented in ThemeMaker's {@code BlockRenderer.tsx}.
 *
 * <p>Port of ThemeMaker's {@code BlockRenderer} —
 * {@code lms-portal/src/components/block-system/BlockRenderer.tsx}.
 * Adapted for our model:
 * <ul>
 *   <li>{@code tenantSlug} dropped — our portal resolves Organisation
 *       from the request Host header server-side and doesn't prefix
 *       URLs with a tenant segment. Hosts that need URL prefixing wrap
 *       block renderers themselves.</li>
 *   <li>{@code blockBleedsUnderHeader} metadata is host-supplied via
 *       the {@code firstBlockBleeds} callback (default = always false).
 *       ThemeMaker keyed bleed off a baked-in metadata map; here it's
 *       a function the host overrides with its own block-metadata
 *       source.</li>
 * </ul>
 */
export interface BlockRendererProps {
  /** Template definition — only the {@code slots} list is read here. */
  template: { slots: SlotSpec[] };
  /** Page composition payload — {@code Page.publishedConfig} or {@code .draftConfig}. */
  config: PageConfig;
  /** Active layout id from {@code Theme.themeId} — drives theme overrides. */
  themeId?: string | null;
  /** Whatever the host wants in {@link AuthContext} — opaque to the renderer. */
  currentUser?: unknown;
  locale?: string;
  fallbackLocale?: string;
  /**
   * Host-supplied per-block check — returns true when the block type's
   * implementation expects to bleed under a floating overlay header.
   * Only the first rendered block ever gets the marker attribute even
   * when true (only the leading block can reasonably bleed).
   */
  firstBlockBleeds?: (blockType: string) => boolean;
}

export function BlockRenderer({
  template,
  config,
  themeId,
  currentUser,
  locale,
  fallbackLocale,
  firstBlockBleeds,
}: BlockRendererProps) {
  const loc = locale ?? 'en';
  const fb = fallbackLocale ?? 'en';
  const authedUser = (currentUser ?? null) as CurrentUser | null;
  const enabledPlugins = useEnabledPlugins();

  const firstBlock = findFirstBlock(template.slots, config, authedUser, enabledPlugins);
  const isFirstBleeding = !!firstBlock && !!firstBlockBleeds && firstBlockBleeds(firstBlock.type);

  return (
    <AuthContext.Provider value={currentUser ?? null}>
      <ThemeContext.Provider value={themeId ?? null}>
        <LocaleContext.Provider value={{ locale: loc, fallback: fb }}>
          {template.slots.map((slot) => {
            const value = config.slots?.[slot.name];
            const items: BlockInstance[] = toArray(value).filter((b) =>
              blockIsVisible(b, authedUser, enabledPlugins),
            );
            if (items.length === 0) return null;
            return (
              <div key={slot.name} data-slot={slot.name}>
                {items.map((b, i) => {
                  const isFirstOfPage = b === firstBlock;
                  return (
                    <BlockErrorBoundary key={`${slot.name}-${i}`} blockType={b.type}>
                      <BlockWrapper bleed={isFirstOfPage && isFirstBleeding}>
                        <RenderOneBlock instance={b} />
                      </BlockWrapper>
                    </BlockErrorBoundary>
                  );
                })}
              </div>
            );
          })}
        </LocaleContext.Provider>
      </ThemeContext.Provider>
    </AuthContext.Provider>
  );
}

/**
 * Wrapper that adds {@code data-bleeds-under-header="true"} when the
 * contained block is the first on the page AND its type is bleed-
 * capable. When {@code bleed} is false the wrapper is a fragment (no
 * extra DOM node).
 */
function BlockWrapper({ bleed, children }: { bleed: boolean; children: React.ReactNode }) {
  if (!bleed) return <>{children}</>;
  return <div data-bleeds-under-header="true">{children}</div>;
}

/**
 * Render a single block instance. Exported so themed layouts and helper
 * components ({@code BlockList}, etc.) can render one block without
 * re-implementing the resolve + localize pipeline.
 */
export function RenderOneBlock({ instance }: { instance: BlockInstance }) {
  const resolver = useBlockResolver();
  const themeId = useThemeIdInline();
  const { locale, fallback } = useLocaleInline();
  const Component = resolver.resolveBlock(themeId, instance.type);
  if (!Component) {
    return <UnknownBlockPlaceholder type={instance.type} />;
  }
  const localized = localizeTree(instance.props ?? {}, locale, fallback) as Record<string, unknown>;

  const advanced =
    localized._advanced &&
    typeof localized._advanced === 'object' &&
    !Array.isArray(localized._advanced)
      ? (localized._advanced as Record<string, unknown>)
      : {};
  const flat: Record<string, unknown> = { ...advanced, ...localized };
  delete flat._advanced;
  // Reserved visibility gate (see BlockVisibilityProps) is consulted by
  // BlockRenderer/NestedSlot BEFORE this component ever renders — strip
  // it here so it doesn't leak onto the block component as an unused prop.
  delete flat._visibility;
  // Reserved identity keys (see blockIdentity.tsx / blockId.ts) — the
  // operator anchor slug, plus the per-instance id + family that the tree
  // walker computed and threaded in. Consumed here into
  // BlockIdentityContext (→ data-<ns>-* attributes on the block root);
  // stripped so they never reach the block component as props.
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const anchor = str(flat._anchor);
  const id = str(flat._gbtId);
  const group = str(flat._gbtGroup);
  delete flat._anchor;
  delete flat._nodeId;
  delete flat._gbtId;
  delete flat._gbtGroup;

  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(flat)) {
    if (v !== '' && v !== null && v !== undefined) cleaned[k] = v;
  }

  // Pre-render any prop that contains BlockInstance(s). A BlockInstance
  // is strictly identified as { type: string, props: object } — generic
  // data arrays (pills, stats, items, tabs, …) whose elements lack a
  // `props` field are NOT affected.
  const withRenderedSlots: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cleaned)) {
    if (isBlockInstanceArray(v)) {
      withRenderedSlots[k] = <NestedSlot key={k} items={v as BlockInstance[]} />;
    } else if (isSingleBlockInstance(v)) {
      withRenderedSlots[k] = <NestedSlot key={k} items={[v as BlockInstance]} />;
    } else if (Array.isArray(v) && v.length === 0 && CONTAINER_SLOT_NAMES.has(k)) {
      // Empty known slot — pass null so the component gets nothing to render.
      withRenderedSlots[k] = null;
    } else {
      withRenderedSlots[k] = v;
    }
  }

  return (
    <BlockIdentityContext.Provider value={{ type: instance.type, id, group, anchor }}>
      {/*
       * Reset the "root already stamped" guard at every block boundary. The
       * guard exists so a block whose own root wrapper nests inside another
       * (<AkSection><BlockRoot as="aside">…) stamps identity once — but a
       * CONTAINER block (Band / Columns / …) renders its children's
       * <BlockRoot>s inside its own, and each of those is a DIFFERENT block
       * instance that must stamp its own data-<ns>-block. Without this reset
       * the container's <BlockRootStamped> leaks down and every nested block
       * renders a bare, identity-less root.
       */}
      <BlockRootStampedContext.Provider value={false}>
        <Component {...withRenderedSlots} />
      </BlockRootStampedContext.Provider>
    </BlockIdentityContext.Provider>
  );
}

/**
 * Renders an array of BlockInstance objects as nested content inside a
 * container block (Columns col1..col4, Band content, Container content, …).
 * Declared as a proper React component so hooks work correctly.
 */
function NestedSlot({ items }: { items: BlockInstance[] }) {
  const authedUser = useCurrentUser<CurrentUser>();
  const enabledPlugins = useEnabledPlugins();
  const visible = items.filter((b) => blockIsVisible(b, authedUser, enabledPlugins));
  return (
    <>
      {visible.map((instance, i) => (
        <BlockErrorBoundary key={i} blockType={instance.type}>
          <RenderOneBlock instance={instance} />
        </BlockErrorBoundary>
      ))}
    </>
  );
}

/**
 * Known container-slot prop names across all layout blocks.
 * Only these props are candidates for BlockInstance[] → ReactNode conversion.
 * This prevents generic array props (pills, items, tabs, …) from being
 * misidentified as nested block lists.
 */
const CONTAINER_SLOT_NAMES = new Set([
  // Columns
  'col1',
  'col2',
  'col3',
  'col4',
  // Band, Container
  'content',
  // generic slot names used by future layout blocks
  'slot',
  'children',
  'header',
  'footer',
  'sidebar',
]);

/** True when v is a non-empty array of block instances ({ type: string, props: object }[]). */
function isBlockInstanceArray(v: unknown): boolean {
  if (!Array.isArray(v) || v.length === 0) return false;
  return v.every(isBlockInstance);
}

/** True when v is a single block instance: { type: string, props: object }. */
function isBlockInstance(v: unknown): v is BlockInstance {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const b = v as Record<string, unknown>;
  return (
    typeof b.type === 'string' &&
    b.type.length > 0 &&
    b.props !== undefined &&
    b.props !== null &&
    typeof b.props === 'object' &&
    !Array.isArray(b.props)
  );
}

/** True when v is a single block instance object (not an array). */
function isSingleBlockInstance(v: unknown): v is BlockInstance {
  return !Array.isArray(v) && isBlockInstance(v);
}

// Inline imports so the file stays self-contained without a circular
// import of the hooks back through the contexts module. They're the
// same hooks exported from contexts.tsx — duplicated as a one-liner
// each to keep this file's import graph shallow.
function useThemeIdInline(): string | null {
  return useThemeId();
}
function useLocaleInline() {
  return useLocale();
}

function UnknownBlockPlaceholder({ type }: { type: string }) {
  const { t } = useTranslation('site-builder-runtime');
  return (
    <div
      style={{
        margin: 16,
        padding: 12,
        border: '1px dashed #d1d5db',
        borderRadius: 8,
        color: '#6b7280',
        fontSize: 13,
        background: '#fafafa',
      }}
    >
      <Trans t={t} i18nKey="block.unknownType" values={{ type }} components={{ c: <code /> }} />
    </div>
  );
}

function findFirstBlock(
  slots: SlotSpec[],
  config: PageConfig,
  user: CurrentUser | null,
  enabledPlugins: ReadonlySet<string> | null,
): BlockInstance | null {
  for (const slot of slots) {
    const items = toArray(config.slots?.[slot.name]).filter((b) =>
      blockIsVisible(b, user, enabledPlugins),
    );
    if (items.length > 0) return items[0] ?? null;
  }
  return null;
}

/**
 * Whether a block INSTANCE passes its own {@code _visibility} gate
 * (see {@link BlockVisibilityProps} for why it's nested there instead
 * of flat top-level props).
 */
function blockIsVisible(
  b: BlockInstance,
  user: CurrentUser | null,
  enabledPlugins: ReadonlySet<string> | null,
): boolean {
  const visibility = b.props?._visibility;
  if (!visibility || typeof visibility !== 'object') return true;
  return isVisible(visibility as BlockVisibilityProps, user, enabledPlugins ?? undefined);
}

function toArray(v: SlotValue): BlockInstance[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.filter((b) => b && b.type);
  return [v];
}
